import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { allProducts, productCategories } from '@/mocks/products';
import { useCart } from '@/context/CartContext';
import type { Product } from '@/mocks/products';
import SEO from '@/components/SEO';

const PAGE_SIZE = 25;

const categoryFilters = ['All', ...productCategories.map((c) => c.name)];

// Maps display category name → internal product.category value
const categoryMap: Record<string, string> = {
  'Garam Masala': 'Spices',
  'Organic Flour': 'Flour',
  'Organic Oils': 'Oils',
  'Organic Seeds': 'Seeds',
  'Recipe Masalas': 'Masalas',
  'Super Foods': 'Superfoods',
  'Remedies': 'Remedies',
  'Honey': 'Honey',
};

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://pearlfoods.pk';

/* ── Product Card ── */
const ProductCard = ({
  product,
  fromUrl,
}: {
  product: Product;
  fromUrl: string;
}) => {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [added, setAdded] = useState(false);
  const defaultSize = product.sizes[1] ?? product.sizes[0];
  const isContactForPrice = defaultSize.contactForPrice === true;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isContactForPrice) return;
    addItem(product, defaultSize);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleCardClick = () => {
    navigate(`/products/${product.id}`, { state: { from: fromUrl } });
  };

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform duration-300 cursor-pointer"
      onClick={handleCardClick}
    >
      {/* Image */}
      <div className="relative w-full overflow-hidden bg-brand-beige/30" style={{ aspectRatio: '1/1' }}>
        <img
          src={product.image}
          alt={product.name}
          className="absolute inset-0 w-full h-full object-contain p-4"
          loading="lazy"
        />
        <span className="absolute top-3 left-3 bg-brand-olive text-white text-xs font-sans font-medium px-2.5 py-1 rounded-full">
          {product.tag}
        </span>
        <span className="absolute top-3 right-3 bg-white/90 text-brand-brown text-xs font-sans px-2.5 py-1 rounded-full">
          {product.category}
        </span>
      </div>

      {/* Info */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-serif text-brand-brown-dark text-base font-semibold">
            {product.name}
          </h3>
          <span className="text-brand-brown/40 text-xs font-sans mt-1 whitespace-nowrap">
            {defaultSize.label}
          </span>
        </div>
        <p className="font-sans text-xs text-brand-olive italic mb-2">{product.benefit}</p>
        <p className="font-sans text-xs text-brand-brown/60 leading-relaxed mb-3 line-clamp-2">
          {product.description}
        </p>

        {/* Price / CTA */}
        {isContactForPrice ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-1">
              <span className="font-serif text-sm font-semibold text-brand-brown-dark">
                Contact for Price
              </span>
              <span className="font-sans text-xs text-brand-brown/40">/{defaultSize.label}</span>
            </div>
            <a
              href={`https://wa.me/923445299984?text=I'm%20interested%20in%20${encodeURIComponent(product.name)}.%20Please%20share%20the%20price.`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="w-full flex items-center justify-center gap-2 font-sans text-sm font-medium px-4 py-2.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap bg-[#25D366] hover:bg-[#20b858] text-white"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-whatsapp-line"></i>
              </div>
              Ask Price on WhatsApp
            </a>
          </div>
        ) : (
          <>
            {/* Price */}
            <div className="flex items-center justify-between mb-4">
              <span className="font-serif text-lg font-bold text-brand-brown-dark">
                Rs. {defaultSize.price.toLocaleString()}
              </span>
              <span className="font-sans text-xs text-brand-brown/40">/{defaultSize.label}</span>
            </div>

            <button
              onClick={handleAddToCart}
              className={`w-full flex items-center justify-center gap-2 font-sans text-sm font-medium px-4 py-2.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                added
                  ? 'bg-brand-gold text-white'
                  : 'bg-brand-olive hover:bg-brand-olive-dark text-white'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={added ? 'ri-check-line' : 'ri-shopping-basket-2-line'}></i>
              </div>
              {added ? 'Added to Cart!' : 'Add to Cart'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

/* ── Main Page ── */
const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const resolveFilter = useCallback(() => {
    const cat = searchParams.get('category') || 'All';
    return categoryFilters.includes(cat) ? cat : 'All';
  }, [searchParams]);

  const [activeFilter, setActiveFilter] = useState(resolveFilter);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Sync filter when URL param changes (browser back/forward)
  useEffect(() => {
    const resolved = resolveFilter();
    setActiveFilter(resolved);
    setVisibleCount(PAGE_SIZE);
  }, [searchParams, resolveFilter]);

  const handleFilterChange = (cat: string) => {
    setActiveFilter(cat);
    setVisibleCount(PAGE_SIZE);
    if (cat === 'All') {
      setSearchParams({});
    } else {
      setSearchParams({ category: cat });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Build filtered list
  const filtered =
    activeFilter === 'All'
      ? allProducts
      : allProducts.filter(
          (p) => p.category === (categoryMap[activeFilter] ?? activeFilter)
        );

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  // Current URL to pass as "from" state to product detail
  const currentUrl =
    activeFilter === 'All'
      ? '/products'
      : `/products?category=${encodeURIComponent(activeFilter)}`;

  const canonicalPath = activeFilter === 'All' ? '/products' : `/products?category=${encodeURIComponent(activeFilter)}`;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: activeFilter === 'All' ? 'All Products' : `${activeFilter} Products`,
      description: 'Explore our complete range of 100% organic, handcrafted wellness products from Pearl Foods Islamabad.',
      url: `${siteUrl}${canonicalPath}`,
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: visible.map((product, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Product',
            name: product.name,
            description: product.description,
            image: product.image,
            url: `${siteUrl}/products/${product.id}`,
            brand: {
              '@type': 'Brand',
              name: 'Pearl Foods',
            },
            offers: product.sizes[0]?.contactForPrice
              ? undefined
              : {
                  '@type': 'Offer',
                  priceCurrency: 'PKR',
                  price: product.sizes[0]?.price,
                  availability: 'https://schema.org/InStock',
                  url: `${siteUrl}/products/${product.id}`,
                },
          },
        })),
      },
    },
  ];

  return (
    <div>
      <SEO
        title={activeFilter === 'All' ? 'Shop Organic Products | Pearl Foods Islamabad' : `${activeFilter} | Pearl Foods Islamabad`}
        description="Explore our complete range of 100% organic, handcrafted wellness products including spices, oils, flour, seeds, masalas, superfoods, remedies and honey from Islamabad."
        keywords="organic products, handmade spices, organic oils, gluten free flour, organic seeds, recipe masalas, superfoods, natural remedies, pure honey Islamabad"
        canonicalPath={canonicalPath}
        jsonLd={jsonLd}
      />
      <Navbar />

      {/* Hero Banner */}
      <section className="relative pt-28 md:pt-36 pb-20 bg-brand-olive-dark overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=organic%20spices%20seeds%20herbs%20arranged%20overhead%20flat%20lay%20rustic%20wooden%20surface%20natural%20earthy%20warm%20tones%20beautiful%20abundance%20nature&width=1600&height=500&seq=pfp001&orientation=landscape"
            alt="Products background"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-block font-sans text-xs tracking-[0.25em] uppercase text-brand-gold font-medium mb-4">
            Our Collection
          </span>
          <h1 className="font-serif text-4xl md:text-6xl text-brand-cream font-bold mb-4">
            Pure, Handmade Products
          </h1>
          <p className="font-sans text-brand-beige/70 text-base md:text-lg max-w-xl mx-auto">
            Explore our complete range of 100% organic, handcrafted wellness products.
          </p>
        </div>
      </section>

      {/* Filter Tabs — sticky, wraps on mobile */}
      <section className="sticky top-16 md:top-20 z-40 bg-brand-cream/95 backdrop-blur-md border-b border-brand-beige py-3 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Mobile: wrap layout (no scroll) */}
          <div className="flex flex-wrap gap-2 md:hidden">
            {categoryFilters.map((cat) => (
              <button
                key={cat}
                onClick={() => handleFilterChange(cat)}
                className={`font-sans text-sm font-medium px-4 py-1.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeFilter === cat
                    ? 'bg-brand-olive text-white'
                    : 'bg-brand-beige/60 text-brand-brown hover:bg-brand-beige'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          {/* Desktop: single row scroll */}
          <div className="hidden md:flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide flex-nowrap">
            {categoryFilters.map((cat) => (
              <button
                key={cat}
                onClick={() => handleFilterChange(cat)}
                className={`flex-shrink-0 font-sans text-sm font-medium px-5 py-2 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeFilter === cat
                    ? 'bg-brand-olive text-white'
                    : 'bg-brand-beige/60 text-brand-brown hover:bg-brand-beige'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-brand-cream">
        <div className="max-w-7xl mx-auto">
          <p className="font-sans text-sm text-brand-brown/50 mb-8">
            Showing{' '}
            <strong className="text-brand-brown">{visible.length}</strong>
            {' '}of{' '}
            <strong className="text-brand-brown">{filtered.length}</strong>{' '}
            products
            {activeFilter !== 'All' && (
              <span className="ml-1">in <em>{activeFilter}</em></span>
            )}
          </p>

          {filtered.length === 0 ? (
            <div className="text-center py-24">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-brand-brown/30">
                <i className="ri-inbox-line text-5xl"></i>
              </div>
              <p className="font-serif text-xl text-brand-brown/50">No products found</p>
            </div>
          ) : (
            <>
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                data-product-shop
              >
                {visible.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    fromUrl={currentUrl}
                  />
                ))}
              </div>

              {/* Load More */}
              {hasMore && (
                <div className="mt-12 text-center">
                  <p className="font-sans text-sm text-brand-brown/50 mb-4">
                    Showing {visible.length} of {filtered.length} products
                  </p>
                  <button
                    onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 bg-brand-olive hover:bg-brand-olive-dark text-white font-sans font-medium px-8 py-3 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-down-line"></i>
                    </div>
                    Load More Products
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Category Sections */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl text-brand-brown-dark font-bold mb-3">
              Browse by Category
            </h2>
            <div className="w-12 h-0.5 bg-brand-gold mx-auto"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {productCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleFilterChange(cat.name)}
                className="group text-center cursor-pointer"
              >
                <div className="relative rounded-xl overflow-hidden h-32 w-full mb-3">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-brand-brown-dark/40 group-hover:bg-brand-brown-dark/20 transition-colors duration-300"></div>
                </div>
                <p className="font-serif text-brand-brown-dark text-sm font-semibold group-hover:text-brand-olive transition-colors duration-200">
                  {cat.name}
                </p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-brand-beige-light px-4 md:px-8 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-2xl md:text-3xl text-brand-brown-dark font-bold mb-4">
            Can&apos;t find what you&apos;re looking for?
          </h2>
          <p className="font-sans text-brand-brown/70 text-sm mb-6">
            Chat with us directly on WhatsApp — we&apos;ll help you find the perfect natural product for your needs.
          </p>
          <a
            href="https://wa.me/923445299984?text=I'm%20looking%20for%20a%20product%20and%20need%20help"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-brand-olive hover:bg-brand-olive-dark text-white font-sans font-medium px-7 py-3.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-whatsapp-line"></i>
            </div>
            Chat with Us
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductsPage;